import React, { useEffect } from 'react';
import { DialogContentStyled, Label, LeftContentWrapper, RightContentWrapper } from 'components/Dialog/DialogContent';
import { LeftContent } from 'components/Dialog/DialogLeftContent';
import { Bottom, BottomActions, ConnectButton, RightForm } from 'components/Dialog/DialogRightContent';
import { ErrorMessage, Input, InputGrid } from 'components/Form';
import { SubmitHandler, useForm } from 'react-hook-form';
import { blibliAuthorize } from 'api/marketPlaces';
import { useAppStore } from 'stores/appStore';
import { channels } from 'shared/constants';
import { showAlert } from 'components/Alert';

import HorizontalDividers from 'components/CustomDividers/HorizontalDividers';
import VerticalDividers from 'components/CustomDividers/VerticalDividers';
import WarningIcon from '@mui/icons-material/Warning';
import config from 'config';
import { AsyncButton } from 'components/ButtonNew/Button';
import { set } from 'date-fns';

interface FormValues {
  store_name: string;
  merchant_id: string;
  api_password: string;
}

interface Props {
  chData?: {
    store_name?: string;
    extra_info: {
      apiSellerKey: string;
      merchantId: string;
    };
  }
  isConnected?: boolean;
  handleClosePrimary: () => void;
  getListChannel: () => void;
}

const BlibliContent: React.FunctionComponent<Props> = ({ chData, isConnected = false, handleClosePrimary, getListChannel }) => {
  const { setShowConnectedPopup, disablePrevButton } = useAppStore();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<FormValues>();
  const [busy, setBusy] = React.useState<boolean>(false);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setBusy(true);
    try {
      const integrateData = {
        merchantId: data?.merchant_id,
        apiSellerKey: data?.api_password,
        mergeOrder: true,
        update: isConnected
      };
      const res = await blibliAuthorize(integrateData);
      if (res && res.data && res.data.status === 'error') {
        showAlert('error', res.data.message, 5000);
      } else {
        if (!disablePrevButton) {
          setShowConnectedPopup(true);
          getListChannel();
          handleClosePrimary();
        } else {
          location.replace(config.baseUrl);
        }
      }
    } catch (err) {
      const error = err as Error;
      console.error(error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isConnected && chData?.store_name) {
      // Set the store name from the connected data
      setValue('store_name', chData.store_name);
    }
    if (isConnected && chData?.extra_info?.merchantId) {
      // Set the merchant ID from the connected data
      setValue('merchant_id', chData.extra_info.merchantId);
    }
    if (isConnected && chData?.extra_info?.apiSellerKey) {
      // Set the API password from the connected data
      setValue('api_password', chData.extra_info.apiSellerKey);
    }
  }, [isConnected, chData, setValue]);

  return (
    <DialogContentStyled>
      <LeftContentWrapper>
        <LeftContent channelId={channels.BLIBLI} />
      </LeftContentWrapper>
      <VerticalDividers />
      <RightContentWrapper>
        <RightForm id="blibli_form" onSubmit={handleSubmit(onSubmit)}>
          <InputGrid
            label={<Label>Nama Toko</Label>}
            input={
              <Input
                isError={errors.store_name?.message ? true : false}
                type={'text'}
                placeholder={'Masukkan Nama Toko'}
                disabled={isConnected}
                {...register('store_name', {
                  required: { value: true, message: 'Nama Toko harus di isi' }
                })}
              />
            }
            errorMessage={
              errors?.store_name && (
                <ErrorMessage
                  icon={<WarningIcon fontSize="small" color="error" />}
                  message={errors.store_name.message}
                />
              )
            }
          />
          <InputGrid
            label={<Label className="requiredField">ID Merchant</Label>}
            input={
              <Input
                isError={errors.merchant_id?.message ? true : false}
                type={'text'}
                placeholder={'ID Merchant'}
                disabled={isConnected}
                {...register('merchant_id', {
                  required: { value: true, message: 'ID Merchant harus di isi' }
                })}
              />
            }
            errorMessage={
              errors?.merchant_id && (
                <ErrorMessage
                  icon={<WarningIcon fontSize="small" color="error" />}
                  message={errors.merchant_id.message}
                />
              )
            }
          />
          <InputGrid
            label={<Label className="requiredField">Kata Sandi API</Label>}
            input={
              <Input
                isError={errors.api_password?.message ? true : false}
                type={'password'}
                placeholder={'Kata Sandi API'}
                disabled={isConnected}
                {...register('api_password', {
                  required: { value: true, message: 'Kata Sandi API harus di isi' }
                })}
              />
            }
            errorMessage={
              errors?.api_password && (
                <ErrorMessage
                  icon={<WarningIcon fontSize="small" color="error" />}
                  message={errors.api_password.message}
                />
              )
            }
          />
        </RightForm>
        <Bottom>
          <HorizontalDividers />
          <BottomActions>
            {isConnected && (
              <AsyncButton busy={busy} variant="outlined" color='inherit' type="submit" form="blibli_form" style={{ padding: '10px 1rem' }}>Otorisasi Ulang</AsyncButton>
            )}
            <AsyncButton disabled={isConnected} variant="contained" color='primary' type="submit" form="blibli_form" style={{ padding: '10px 1rem' }}>Sambungkan</AsyncButton>
          </BottomActions>
        </Bottom>
      </RightContentWrapper>
    </DialogContentStyled>
  );
};

export default BlibliContent;
